import type React from 'react';
import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';

import CustomLoader from 'components/common/CustomLoader';
import InitialDataFetcher from 'components/InitialDataFetcher';
import OrderProvider from 'context/order/OrderContext';
import OrderTimingsProvider from 'context/order-timings/OrderTimingsContext';
import PusherProvider from 'context/pusher';
import CartChannelProvider from 'context/pusher/cart';
import CheckoutChannelProvider from 'context/pusher/checkout';
import CustomerLogoutChannelProvider from 'context/pusher/customer-logout';
import OrderDiscardChannelProvider from 'context/pusher/order-discard';
import PriceChannelProvider from 'context/pusher/price';
import TableChangeChannelProvider from 'context/pusher/table-change';
import TableVerificationChannelProvider from 'context/pusher/table-verification';
import VoidItemChannelProvider from 'context/pusher/void-item';
import VoidOrderChannelProvider from 'context/pusher/void-order';
import WaiterChannelProvider from 'context/pusher/waiter';
import MainLayout from 'layout';

const BillPendingPage = lazy(() => import('pages/bill-pending'));
const CheckoutPage = lazy(() => import('pages/checkout-details-page'));
const CustomerDetailsPage = lazy(() => import('pages/customer-details-page'));
const EnterNamePage = lazy(() => import('pages/enter-name-page'));
const HomePage = lazy(() => import('pages/home-page'));
const OrdersPage = lazy(() => import('pages/order-details-page'));
const OtpVerificationPage = lazy(() => import('pages/otp-verification-page'));
const PaymentConfirmationPage = lazy(() => import('pages/payment-confirmation-page'));
const PaymentInProgressBlockPage = lazy(() => import('pages/payment-in-progress-block-page'));
const PaymentStatusPage = lazy(() => import('pages/payment-status-page'));
const PromotionsDetailsPage = lazy(() => import('pages/promotion-details-page'));
const ReorderPage = lazy(() => import('pages/reorder-page'));
const SearchPage = lazy(() => import('pages/search-page'));
const SessionExpiredPage = lazy(() => import('pages/session-expired-page'));
const TablePinVerificationPage = lazy(() => import('pages/table-pin-verification-page'));
const TermsAndConditionsPage = lazy(() => import('pages/terms-and-conditions-page'));

const RouteCenter: React.FC = () => {
  return (
    <Router>
      <InitialDataFetcher />
      <PusherProvider>
        <OrderTimingsProvider>
          <TableChangeChannelProvider>
            <TableVerificationChannelProvider>
              <PriceChannelProvider>
                <CartChannelProvider>
                  <CheckoutChannelProvider>
                    <WaiterChannelProvider>
                      <OrderDiscardChannelProvider>
                        <CustomerLogoutChannelProvider>
                          <VoidOrderChannelProvider>
                            <VoidItemChannelProvider>
                              <OrderProvider>
                                <MainLayout>
                                  <Suspense fallback={<CustomLoader isLoading />}>
                                    <Routes>
                                      <Route path='/' element={<CustomerDetailsPage />} />
                                      <Route path='/verify-otp' element={<OtpVerificationPage />} />
                                      <Route path='/enter-name' element={<EnterNamePage />} />
                                      <Route
                                        path='/verify-pin'
                                        element={<TablePinVerificationPage />}
                                      />
                                      <Route path='/home' element={<HomePage />} />
                                      <Route path='/search' element={<SearchPage />} />
                                      <Route path='/order' element={<OrdersPage />} />
                                      <Route path='/re-order' element={<ReorderPage />} />
                                      <Route path='/bill-pending' element={<BillPendingPage />} />
                                      <Route path='/check-out' element={<CheckoutPage />} />
                                      <Route
                                        path='/payment-confirmation'
                                        element={<PaymentConfirmationPage />}
                                      />
                                      <Route
                                        path='/payment-in-progress-block'
                                        element={<PaymentInProgressBlockPage />}
                                      />
                                      <Route
                                        path='/payment-status'
                                        element={<PaymentStatusPage />}
                                      />
                                      <Route
                                        path='/promotion-details'
                                        element={<PromotionsDetailsPage />}
                                      />
                                      <Route
                                        path={`/terms/:type`}
                                        element={<TermsAndConditionsPage />}
                                      />
                                      <Route
                                        path='/session-expired'
                                        element={<SessionExpiredPage />}
                                      />
                                    </Routes>
                                  </Suspense>
                                </MainLayout>
                              </OrderProvider>
                            </VoidItemChannelProvider>
                          </VoidOrderChannelProvider>
                        </CustomerLogoutChannelProvider>
                      </OrderDiscardChannelProvider>
                    </WaiterChannelProvider>
                  </CheckoutChannelProvider>
                </CartChannelProvider>
              </PriceChannelProvider>
            </TableVerificationChannelProvider>
          </TableChangeChannelProvider>
        </OrderTimingsProvider>
      </PusherProvider>
    </Router>
  );
};

export default RouteCenter;
